import { useState, type ReactNode } from 'react';
import { ThemeProvider } from 'next-themes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/hooks/use-auth';
import { initAnalytics } from '@/lib/analytics';
import { initMetaPixel } from '@/lib/meta-pixel';

interface Props {
  children: ReactNode;
}

// No módulo, não em effect: precisa rodar antes do primeiro `track()` de
// qualquer página. É idempotente e no-op sem VITE_POSTHOG_KEY.
initAnalytics();
// Mesmo regime: idempotente e no-op sem VITE_META_PIXEL_ID.
initMetaPixel();

export function AppProviders({ children }: Props) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            gcTime: 5 * 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
          mutations: { retry: 0 },
        },
      })
  );

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="clearance-theme"
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <ErrorBoundary>
            <AuthProvider>{children}</AuthProvider>
          </ErrorBoundary>
          <Toaster />
          {import.meta.env.MODE !== 'production' && (
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
          )}
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
